import { Avatar, Button } from '@cue/atoms';
import { useQueryParams } from '@cue/hooks';
import { Modal, useModal } from '@cue/organisms';
import { Global } from '@emotion/react';
import { LocationCategory } from '@project/cms/modules/agenda';
import React from 'react';
import { AgendaDisplayOptions } from '../agenda';
import { AgendaEventProps } from '../agenda-event';
import { fallBackImage } from './event-speaker';
import { SpeakerAgenda } from './speaker-agenda';

interface SpeakerListProps {
  speakers: AgendaEventProps['persons'];
  agendaDisplayOptions?: AgendaDisplayOptions;
  locationCategories?: LocationCategory[];
  hideLocation?: boolean;
  currentSpeaker?: NonNullable<AgendaEventProps['persons']>[0];

  onSpeakerClick?: (speaker: NonNullable<AgendaEventProps['persons']>[0]) => void;
}

export const Speaker: React.FC<SpeakerProps> = ({
  speaker,
  agendaDisplayOptions,
  locationCategories,
  hideLocation,
  disabled,
  onSpeakerClick,
}) => {
  const [modalRef] = useModal();
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [params, setParams] = useQueryParams();

  // currentSpeaker and speaker can be different if the user clicks on a different person in SpeakerAgenda (1) that has been opened by clicking on speaker (from props)
  // We cannot close this modal, because from a React point of view the new SpeakerAgenda (2) modal is a child of SpeakerAgenda (1) and closing SpeakerAgenda (1) would also remove the new SpeakerAgenda (2) modal.
  const [currentSpeaker, setCurrentSpeaker] =
    React.useState<NonNullable<AgendaEventProps['persons']>[0]>(speaker);

  React.useEffect(() => {
    if (isModalOpen) {
      modalRef.current?.open();
    } else {
      modalRef.current?.close();
    }
  }, [isModalOpen, modalRef]);
  if (!speaker) return null;

  return (
    <>
      {/* TODO: Move to core */}
      {isModalOpen && (
        <Global
          styles={{
            body: {
              overflowY: 'hidden',
            },
          }}
        />
      )}
      <Avatar
        speaker={{
          ...speaker,
          occupation: `${speaker.occupation ?? ''} ${speaker.company ? `(${speaker.company})` : ''}`,
          image: speaker.profileImageURL || fallBackImage,
        }}
        overrideCSS={{
          '.cue-avatar-image': {
            cursor:
              agendaDisplayOptions?.speakerAgenda && !disabled ? 'pointer' : 'initial!important',
            '&:hover': {
              // transform: agendaDisplayOptions?.speakerAgenda && !disabled ? 'scale(1.1)' : 'none',
              boxShadow:
                agendaDisplayOptions?.speakerAgenda && !disabled ? '0 0 0 1px #00e6dc' : 'none',
            },
          },
        }}
        onClick={
          agendaDisplayOptions?.speakerAgenda
            ? () => {
                if (onSpeakerClick) {
                  onSpeakerClick(speaker);
                  setCurrentSpeaker(speaker);
                  return;
                }

                setIsModalOpen(true);
              }
            : undefined
        }
      />
      <Modal
        ref={modalRef}
        placement={{ vertical: 'top', horizontal: 'center' }}
        overrideCSS={{
          '.cue-modal-content': { width: '95vw', maxWidth: 1280, background: 'rgb(0, 24, 59)' },
          '.cue-modal-close-button-wrapper': {
            right: '0px',
            top: '0px',
            button: {
              borderRadius: '999px',
              width: '20px',
              height: '30px',
              '&:hover': {
                path: {
                  fill: '#000028',
                },
              },
            },
          },
        }}
        onVisibilityChange={(isOpen) => setIsModalOpen(isOpen)}
        customCloseRenderer={() => (
          <Button
            icon="close"
            styling="close"
            onClick={() => {
              setParams({ ...params, speaker: null });

              if (modalRef?.current?.animatedClose)
                modalRef.current.animatedClose({ className: 'modal-fade-out', duration: 500 });
            }}
          />
        )}>
        <SpeakerAgenda
          speaker={currentSpeaker}
          locationCategories={locationCategories}
          hideLocation={hideLocation}
          onSpeakerClick={(speaker) => {
            setCurrentSpeaker(speaker);
          }}
        />
      </Modal>
      {/* <Modal
        ref={modalRef}
        placement={{ vertical: 'top', horizontal: 'center' }}
        customCloseRenderer={() => (
          <Button
            icon="close"
            styling="modalClosePlain project-resend-access-close"
            onClick={() => {
              if (modalRef?.current?.animatedClose)
                modalRef.current.animatedClose({ className: 'close', duration: 100000 });
            }}
          />
        )}>
        <SpeakerAgenda
          speaker={speaker}
          locationCategories={locationCategories}
          hideLocation={hideLocation}
        />
      </Modal> */}
    </>
  );
};

interface SpeakerProps {
  // TODO: Fix Type
  /* eslint-disable @typescript-eslint/ban-ts-comment */
  // @ts-ignore
  speaker: AgendaEventProps['persons'][0];
  agendaDisplayOptions?: AgendaDisplayOptions;
  locationCategories?: LocationCategory[];
  hideLocation?: boolean;
  currentSpeaker?: NonNullable<AgendaEventProps['persons']>[0];
  onSpeakerClick?: (speaker: NonNullable<AgendaEventProps['persons']>[0]) => void;
  disabled?: boolean;
}

export const SpeakerList: React.FC<SpeakerListProps> = ({
  speakers,
  agendaDisplayOptions,
  locationCategories,
  hideLocation,
  currentSpeaker,
  onSpeakerClick,
}) => {
  if (!speakers) return null;

  return (
    <ul>
      {speakers.map((s) => (
        <li key={s.id}>
          <Speaker
            disabled={currentSpeaker?.id === s.id}
            speaker={s}
            agendaDisplayOptions={{
              ...agendaDisplayOptions,
              speakerAgenda: currentSpeaker?.id !== s.id && agendaDisplayOptions?.speakerAgenda,
            }}
            locationCategories={locationCategories}
            hideLocation={hideLocation}
            onSpeakerClick={onSpeakerClick}
          />
        </li>
      ))}
    </ul>
  );
};
